import React, { useState } from 'react';

const Projects = () => {
  const [activeProject, setActiveProject] = useState(0);

  const projects = [
    {
      id: 1,
      title: "Madrasati - Saas",
      category: "SaaS Development",
      description: "A comprehensive school management system designed to streamline how educational institutions manage students, teachers, and operations.",
      longDescription: "Built a full-stack SaaS platform that handles student enrollment, grade tracking, attendance management, and admin suite. Recently launched and expected to reach $500/month in recurring revenue and 1,000+ users in the coming months.",
      image: "/madrasati-landing.png",
      image2: "/madrasati-landing-2.png",
      isScreenshot: true,
      technologies: ["React", "Next.js", "Node.js", "PostgreSQL", "Supabase", "Stripe", "Twilio"],
      results: [
        "Full-stack platform ready for launch",
        "Expected to reach $500/month revenue soon",
        "Pitched to 3 schools in the first month",
        "Scalable architecture for growth"
      ],
      metrics: {
        users: "1,000+",
        revenue: "$500/mo",
        growth: "+500%"
      },
      timeline: "8 weeks",
      link: "https://madrasati.tech"
    },
    {
      id: 2,
      title: "SCAYA - Website",
      category: "Website",
      description: "A high-converting website for an athletic association that generated 50 sign-up requests and 20 new members in the first month.",
      longDescription: "Designed and developed a conversion-optimized website that tells the story of the athletic association, showcases programs, and drives membership sign-ups with compelling CTAs and social proof. Since launch, generated 50 sign-up requests with 20 new members converted in the first month, along with heavy site traffic.",
      image: "/scaya-landing.png",
      image2: "/scaya-landing-2.png",
      isScreenshot: true,
      technologies: ["React", "Tailwind CSS", "Mobbin", "Google Forms"],
      results: [
        "50 sign-up requests in first month",
        "20 new members converted (40% conversion)",
        "Heavy traffic to the site",
        "Strong engagement and interest"
      ],
      metrics: {
        signups: "50",
        members: "20",
        conversion: "40%"
      },
      timeline: "1 weeks",
      link: "https://scaya2020.org"
    },
    {
      id: 3,
      title: "Tech Talented - Website",
      category: "Website",
      description: "A modern, conversion-focused portfolio website showcasing software consulting services and past projects.",
      longDescription: "Created a sophisticated portfolio website with interactive project showcases, client testimonials, and optimized contact forms. Features modern animations and responsive design.",
      image: "/tt-landing.png",
      image2: "/tt-landing-2.png",
      isScreenshot: true,
      technologies: ["React", "Tailwind CSS", "Modern UI", "Responsive Design"],
      results: [
        "Modern design implementation",
        "Interactive project gallery",
        "Optimized contact forms",
        "Mobile-first responsive design"
      ],
      metrics: {
        design: "Modern",
        responsive: "100%",
        interactive: "Yes"
      },
      timeline: "2 weeks",
      link: "#"
    },
  ];

  return (
    <section id="projects" className="py-20 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" viewBox="0 0 60 60">
          <defs>
            <pattern id="project-pattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <circle cx="30" cy="30" r="2" fill="currentColor" />
              <circle cx="10" cy="10" r="1" fill="currentColor" />
              <circle cx="50" cy="50" r="1" fill="currentColor" />
            </pattern>
          </defs>
          <rect width="60" height="60" fill="url(#project-pattern)" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="mb-6">
            <span className="inline-block px-4 py-2 bg-purple-100 text-purple-800 text-sm font-semibold rounded-full">
              🚀 Our Portfolio
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Featured Projects
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Discover how we've helped startups and businesses build successful SaaS platforms and high-converting websites.
          </p>
        </div>

        {/* Project Showcase */}
        <div className="mb-16">
          {/* Project Navigation */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {projects.map((project, index) => (
              <button
                key={project.id}
                onClick={() => setActiveProject(index)}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  activeProject === index
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {project.title}
              </button>
            ))}
          </div>

          {/* Active Project Display */}
          <div className="bg-gray-50 rounded-3xl p-8 md:p-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Project Visual */}
              <div className="order-2 lg:order-1">
                {projects[activeProject].isScreenshot ? (
                  <div className="space-y-6">
                    {/* First Image */}
                    <div className="relative bg-white rounded-2xl p-4 shadow-2xl border border-gray-200">
                      <img 
                        src={projects[activeProject].image} 
                        alt={projects[activeProject].title}
                        className="w-full h-auto rounded-xl shadow-lg"
                      />
                      {/* Screenshot Badge */}
                      <div className="absolute -top-3 -right-3 bg-gradient-to-r from-green-500 to-teal-500 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                        Live Project
                      </div>
                      {/* Floating Elements */}
                      <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue-500 rounded-xl flex items-center justify-center shadow-xl transform rotate-12">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                    {/* Second Image */}
                    <div className="relative bg-white rounded-2xl p-4 shadow-2xl border border-gray-200">
                      <img 
                        src={projects[activeProject].image2} 
                        alt={projects[activeProject].title}
                        className="w-full h-auto rounded-xl shadow-lg"
                      />
                      {/* Floating Elements */}
                      <div className="absolute -bottom-4 -right-4 w-16 h-16 bg-purple-500 rounded-xl flex items-center justify-center shadow-xl transform -rotate-12">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`relative bg-gradient-to-br ${projects[activeProject].gradient} rounded-2xl p-12 text-white text-center shadow-2xl`}>
                    <div className="text-8xl mb-6">{projects[activeProject].image}</div>
                    <h3 className="text-2xl font-bold mb-2">{projects[activeProject].title}</h3>
                    <p className="text-lg opacity-90">{projects[activeProject].category}</p>
                    
                    {/* Floating Elements */}
                    <div className="absolute -top-4 -right-4 w-16 h-16 bg-white bg-opacity-20 rounded-xl backdrop-blur-sm flex items-center justify-center">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-white bg-opacity-20 rounded-full backdrop-blur-sm"></div>
                  </div>
                )}
              </div>

              {/* Project Details */}
              <div className="order-1 lg:order-2">
                <div className="mb-6">
                  <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full mb-4">
                    {projects[activeProject].category}
                  </span>
                  <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {projects[activeProject].title}
                  </h3>
                  <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                    {projects[activeProject].longDescription}
                  </p>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-3 gap-4 mb-8">
                  {Object.entries(projects[activeProject].metrics).map(([key, value]) => (
                    <div key={key} className="text-center p-4 bg-white rounded-xl shadow-md">
                      <div className="text-2xl font-bold text-blue-600">{value}</div>
                      <div className="text-sm text-gray-600 capitalize">{key}</div>
                    </div>
                  ))}
                </div>

                {/* Results */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Key Results:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {projects[activeProject].results.map((result, index) => (
                      <div key={index} className="flex items-center">
                        <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700">{result}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Technologies */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Technologies:</h4>
                  <div className="flex flex-wrap gap-2">
                    {projects[activeProject].technologies.map((tech, index) => (
                      <span key={index} className="px-3 py-1 bg-gray-200 text-gray-800 text-sm rounded-full">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Timeline */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-gray-500">Delivery Time:</span>
                    <div className="text-lg font-semibold text-gray-900">{projects[activeProject].timeline}</div>
                  </div>
                  <a 
                    href={projects[activeProject].link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors inline-block"
                  >
                    View Live Project
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 md:p-12 text-white">
          <h3 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Build Your Success Story?
          </h3>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Let's discuss your project and see how we can help you achieve similar results.
          </p>
          <button
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="bg-white text-blue-600 hover:bg-gray-100 font-bold py-4 px-8 rounded-xl transition-colors shadow-lg hover:shadow-xl"
          >
            Start Your Project
          </button>
        </div>
      </div>
    </section>
  );
};

export default Projects;

